/**
 * Documents3 Page - Entity-First Document Analysis
 *
 * Redesigned to match Sources detail view with:
 * - Domain grouping (Market, Business, Solution, Insights)
 * - Auto-accepted entities
 * - Browsable entity list with detail panel
 * - AI metadata display
 */

import { useState, useRef, useMemo } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { useSettingsStore } from '@/stores/settingsStore'
import {
  docsApi,
  type Document2WithEntities,
  type Document2Entity,
  type ContentType,
} from '@/lib/api/client'
import { extractTextFromPdf } from '@/lib/pdf/extractor'
import { CONTENT_TYPES, CONTENT_TYPE_ICONS } from '@/lib/analyzer/types'
import { useDocuments3Processing } from '@/hooks/useDocuments3Processing'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  FileText,
  Plus,
  Loader2,
  AlertCircle,
  Zap,
  Clock,
  Cpu,
  Hash,
  CheckCircle2,
  ChevronRight,
  ChevronDown,
  X,
  Link2,
  TrendingUp,
  Building2,
  Lightbulb,
  Target,
  List,
  FileCode,
  FileImage,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { PdfEvidenceViewer } from './pdf/PdfEvidenceViewer'

// ============================================================================
// DOMAIN CONFIGURATION
// ============================================================================

const DOMAIN_CONFIG = {
  Market: {
    icon: TrendingUp,
    color: 'var(--color-market)',
    bgColor: 'bg-market/10',
    textColor: 'text-market',
    borderColor: 'border-market/30',
    types: [
      'Company', 'Industry', 'Competitor', 'CustomerSegment', 'Persona',
      'Person', 'Stakeholder', 'Problem', 'CustomerPain', 'CustomerGain',
      'CustomerJob', 'Alternative', 'Trend', 'MarketSignal',
    ],
  },
  Business: {
    icon: Building2,
    color: 'var(--color-business)',
    bgColor: 'bg-business/10',
    textColor: 'text-business',
    borderColor: 'border-business/30',
    types: [
      'ValueProposition', 'Channel', 'RevenueStream', 'KeyResource',
      'KeyActivity', 'KeyPartnership', 'CostStructure', 'Strategy', 'StrategicGoal',
    ],
  },
  Solution: {
    icon: Lightbulb,
    color: 'var(--color-solution)',
    bgColor: 'bg-solution/10',
    textColor: 'text-solution',
    borderColor: 'border-solution/30',
    types: ['Product', 'Feature', 'Requirement', 'Constraint'],
  },
  Insights: {
    icon: Target,
    color: 'var(--color-text-secondary)',
    bgColor: 'bg-warm-100',
    textColor: 'text-text-secondary',
    borderColor: 'border-warm-300',
    types: [], // Catch-all for other types
  },
}

type DomainKey = keyof typeof DOMAIN_CONFIG

function getEntityDomain(entityType: string): DomainKey {
  for (const [domain, config] of Object.entries(DOMAIN_CONFIG)) {
    if (config.types.includes(entityType)) {
      return domain as DomainKey
    }
  }
  return 'Insights'
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export function Documents3Page() {
  const { accountId, projectId } = useSessionStore()
  const { claudeApiKey } = useSettingsStore()
  const [showUploader, setShowUploader] = useState(false)
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null)
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const {
    documents,
    selectedDoc,
    isLoadingDocuments,
    isLoadingDocument,
    extractionProgress,
    error,
    extractAndStore,
    selectDocument,
    clearError,
    refreshDocuments,
  } = useDocuments3Processing({
    autoLoadDocuments: true,
    onDocumentCreated: (doc) => {
      selectDocument(doc.document.id)
      setShowUploader(false)
    },
  })

  // Delete a single document
  const handleDeleteDocument = async (docId: string) => {
    if (!accountId || !projectId) return

    try {
      setIsDeleting(true)
      await docsApi.delete(accountId, projectId, docId)
      setDeleteDocId(null)
      // Clear selection if deleted doc was selected
      if (selectedDoc?.document.id === docId) {
        selectDocument('')
      }
      refreshDocuments()
    } catch (err) {
      console.error('Failed to delete document:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  // Delete all documents
  const handleDeleteAll = async () => {
    if (!accountId || !projectId) return

    try {
      setIsDeleting(true)
      await docsApi.deleteAll(accountId, projectId)
      setShowDeleteAllDialog(false)
      selectDocument('')
      refreshDocuments()
    } catch (err) {
      console.error('Failed to delete all documents:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDocumentCreated = (doc: Document2WithEntities) => {
    selectDocument(doc.document.id)
    setShowUploader(false)
    refreshDocuments()
  }

  if (!accountId || !projectId) {
    return (
      <div className="flex items-center justify-center h-64 text-text-secondary">
        Select a project to view documents
      </div>
    )
  }

  const loading = isLoadingDocuments || isLoadingDocument

  return (
    <div className="flex h-full bg-warm-50">
      {/* Document List Sidebar */}
      <div className="w-72 border-r border-warm-200 bg-warm-50/50 flex flex-col">
        <div className="p-4 border-b border-warm-200 bg-warm-50/80">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-text-primary">Documents3</h2>
              <p className="text-xs text-text-secondary">Entity Extraction</p>
            </div>
            <Button
              size="sm"
              className="bg-solution hover:bg-solution/90"
              onClick={() => setShowUploader(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              New
            </Button>
          </div>
          {documents.length > 0 && (
            <div className="mt-2 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => setShowDeleteAllDialog(true)}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete All
              </Button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto">
          {loading && documents.length === 0 ? (
            <div className="p-4 text-center text-text-secondary">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-solution" />
              Loading...
            </div>
          ) : documents.length === 0 ? (
            <div className="p-4 text-center text-text-secondary">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No documents yet</p>
              <Button
                variant="link"
                size="sm"
                className="text-solution"
                onClick={() => setShowUploader(true)}
              >
                Upload your first document
              </Button>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className={`group flex items-center gap-1 rounded-lg transition-all ${
                    selectedDoc?.document.id === doc.id
                      ? 'bg-solution/10 ring-2 ring-solution'
                      : 'hover:bg-warm-100'
                  }`}
                >
                  <button
                    onClick={() => selectDocument(doc.id)}
                    className="flex-1 text-left p-3"
                  >
                    <div className="font-medium truncate text-text-primary text-sm">
                      {doc.title}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs">
                      <Badge
                        variant="secondary"
                        className="text-xs bg-solution/10 text-solution border-0"
                      >
                        {doc.entityCount} entities
                      </Badge>
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteDocId(doc.id)
                    }}
                    className="p-2 mr-1 opacity-0 group-hover:opacity-100 text-text-muted hover:text-red-500 transition-all"
                    title="Delete document"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden bg-white">
        {selectedDoc ? (
          <EntityBrowser
            doc={selectedDoc}
            extractionProgress={extractionProgress}
            loading={loading}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-text-secondary">
            <div className="text-center">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>Select a document to browse entities</p>
            </div>
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      {showUploader && (
        <DocumentUploader
          onClose={() => setShowUploader(false)}
          onDocumentCreated={handleDocumentCreated}
          claudeApiKey={claudeApiKey}
        />
      )}

      {/* Error Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          {error.message}
          <button onClick={clearError} className="text-white/80 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Delete Single Document Dialog */}
      <Dialog open={!!deleteDocId} onOpenChange={() => setDeleteDocId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this document? This will remove the document and all its extracted entities and evidence. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDocId(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteDocId && handleDeleteDocument(deleteDocId)}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Documents Dialog */}
      <Dialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete All Documents</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>all {documents.length} documents</strong>? This will remove all documents and their extracted entities and evidence. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteAllDialog(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAll}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting All...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All Documents
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ============================================================================
// ENTITY BROWSER - Main content area with domain grouping
// ============================================================================

interface EntityBrowserProps {
  doc: Document2WithEntities
  extractionProgress: ReturnType<typeof useDocuments3Processing>['extractionProgress']
  loading: boolean
}

function EntityBrowser({
  doc,
  extractionProgress,
  loading,
}: EntityBrowserProps) {
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null)
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(
    new Set(['Market', 'Business', 'Solution', 'Insights'])
  )
  const [viewMode, setViewMode] = useState<'list' | 'doc' | 'pdf'>('list')
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set())

  // Group entities by type with counts (for filter chips) - computed first for domain toggle
  const typeCountsByDomain = useMemo(() => {
    const counts: Record<DomainKey, Record<string, number>> = {
      Market: {},
      Business: {},
      Solution: {},
      Insights: {},
    }

    for (const entity of doc.entities) {
      const domain = getEntityDomain(entity.entityType)
      counts[domain][entity.entityType] = (counts[domain][entity.entityType] || 0) + 1
    }

    return counts
  }, [doc.entities])

  // Toggle a single type
  const toggleType = (type: string) => {
    const next = new Set(selectedTypes)
    if (next.has(type)) {
      next.delete(type)
    } else {
      next.add(type)
    }
    setSelectedTypes(next)
  }

  // Toggle all types in a domain
  const toggleDomainFilter = (domain: DomainKey) => {
    const domainTypes = Object.keys(typeCountsByDomain[domain])
    if (domainTypes.length === 0) return

    const next = new Set(selectedTypes)
    const allSelected = domainTypes.every((t) => next.has(t))

    if (allSelected) {
      // Unselect all types in this domain
      domainTypes.forEach((t) => next.delete(t))
    } else {
      // Select all types in this domain
      domainTypes.forEach((t) => next.add(t))
    }
    setSelectedTypes(next)
  }

  // Check if a domain is fully selected
  const isDomainSelected = (domain: DomainKey) => {
    const domainTypes = Object.keys(typeCountsByDomain[domain])
    return domainTypes.length > 0 && domainTypes.every((t) => selectedTypes.has(t))
  }

  // Check if a domain is partially selected
  const isDomainPartiallySelected = (domain: DomainKey) => {
    const domainTypes = Object.keys(typeCountsByDomain[domain])
    const selectedCount = domainTypes.filter((t) => selectedTypes.has(t)).length
    return selectedCount > 0 && selectedCount < domainTypes.length
  }

  // Group entities by domain (with type filter applied)
  const entitiesByDomain = useMemo(() => {
    const grouped: Record<DomainKey, Document2Entity[]> = {
      Market: [],
      Business: [],
      Solution: [],
      Insights: [],
    }

    for (const entity of doc.entities) {
      // Apply type filter if any types are selected
      if (selectedTypes.size > 0 && !selectedTypes.has(entity.entityType)) continue
      const domain = getEntityDomain(entity.entityType)
      grouped[domain].push(entity)
    }

    return grouped
  }, [doc.entities, selectedTypes])

  // All evidence quotes for highlighting in doc view
  const allEvidenceQuotes = useMemo(() => {
    const quotes: { quote: string; entityId: string; entityType: string; entityName: string }[] = []
    for (const entity of doc.entities) {
      // Apply type filter if any types are selected
      if (selectedTypes.size > 0 && !selectedTypes.has(entity.entityType)) continue
      for (const ev of entity.evidence) {
        quotes.push({
          quote: ev.quote,
          entityId: entity.id,
          entityType: entity.entityType,
          entityName: entity.name,
        })
      }
    }
    return quotes
  }, [doc.entities, selectedTypes])

  const selectedEntity = selectedEntityId
    ? doc.entities.find((e) => e.id === selectedEntityId)
    : null

  const pendingCount = doc.entities.filter((e) => e.status === 'pending').length
  const acceptedCount = doc.entities.filter((e) => e.status !== 'pending').length

  const toggleDomain = (domain: string) => {
    const next = new Set(expandedDomains)
    if (next.has(domain)) {
      next.delete(domain)
    } else {
      next.add(domain)
    }
    setExpandedDomains(next)
  }

  // Count total evidence (facets)
  const totalEvidence = doc.entities.reduce((sum, e) => sum + e.evidence.length, 0)

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Status Banner - Graph-only architecture: entities stored in Fuseki */}
      <div className="px-4 py-3 bg-solution/5 border-b border-solution/20 flex items-center justify-between">
        <div>
          <div className="text-solution font-medium flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            {doc.entities.length} entities in knowledge graph
          </div>
          <div className="text-xs text-text-secondary">
            {totalEvidence} evidence quotes extracted and stored in Fuseki
          </div>
        </div>
        {loading && (
          <div className="flex items-center gap-2 text-solution">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        )}
      </div>

      {/* Header with counts and view toggle */}
      <div className="p-3 border-b border-warm-200 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-text-secondary">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-text-muted" />
            {pendingCount} pending
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            {acceptedCount} accepted
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-solution" />
            {doc.entities.filter(e => e.status === 'created').length} entities
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-warm-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                viewMode === 'list'
                  ? 'bg-white shadow-sm text-text-primary font-medium'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <List className="h-4 w-4" />
              List
            </button>
            <button
              onClick={() => setViewMode('doc')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                viewMode === 'doc'
                  ? 'bg-white shadow-sm text-text-primary font-medium'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              <FileCode className="h-4 w-4" />
              Doc
            </button>
            <button
              onClick={() => setViewMode('pdf')}
              disabled={doc.document.fileType !== 'pdf'}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                viewMode === 'pdf'
                  ? 'bg-white shadow-sm text-text-primary font-medium'
                  : doc.document.fileType !== 'pdf'
                    ? 'text-text-muted cursor-not-allowed opacity-50'
                    : 'text-text-secondary hover:text-text-primary'
              }`}
              title={doc.document.fileType !== 'pdf' ? 'No PDF file available' : 'View PDF with annotations'}
            >
              <FileImage className="h-4 w-4" />
              PDF
            </button>
          </div>
        </div>
      </div>

      {/* Type Filter Bar - Stacked by domain */}
      <div className="px-4 py-2 border-b border-warm-200 bg-white">
        <div className="space-y-1.5">
          {/* All button row */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-muted w-16">Filter:</span>
            <button
              onClick={() => setSelectedTypes(new Set())}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                selectedTypes.size === 0
                  ? 'bg-text-primary text-white border-text-primary'
                  : 'bg-white text-text-secondary border-warm-300 hover:border-warm-400'
              }`}
            >
              All ({totalEvidence})
            </button>
          </div>

          {/* Domain rows - stacked vertically */}
          {(Object.entries(DOMAIN_CONFIG) as [DomainKey, typeof DOMAIN_CONFIG.Market][]).map(
            ([domain, config]) => {
              const typeCounts = typeCountsByDomain[domain]
              const types = Object.entries(typeCounts)
              if (types.length === 0) return null

              const domainSelected = isDomainSelected(domain)
              const domainPartial = isDomainPartiallySelected(domain)

              return (
                <div key={domain} className="flex items-center gap-2">
                  {/* Clickable domain label to toggle all types in domain */}
                  <button
                    onClick={() => toggleDomainFilter(domain)}
                    className={`text-xs font-medium w-16 text-left transition-colors ${
                      domainSelected
                        ? config.textColor
                        : domainPartial
                          ? `${config.textColor} opacity-60`
                          : 'text-text-muted hover:text-text-secondary'
                    }`}
                    title={domainSelected ? `Unselect all ${domain}` : `Select all ${domain}`}
                  >
                    {domain}:
                  </button>
                  <div className="flex items-center gap-1 flex-wrap">
                    {types.map(([type]) => {
                      // Count evidence for this type
                      const evidenceCount = doc.entities
                        .filter(e => e.entityType === type)
                        .reduce((sum, e) => sum + e.evidence.length, 0)
                      const isSelected = selectedTypes.has(type)
                      return (
                        <button
                          key={type}
                          onClick={() => toggleType(type)}
                          className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border transition-colors ${
                            isSelected
                              ? `${config.bgColor} ${config.textColor} border-current`
                              : 'bg-white text-text-secondary border-warm-300 hover:border-warm-400'
                          }`}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: config.color }}
                          />
                          {type.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')}_evidence
                          <span className="opacity-60">{evidenceCount}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            }
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {viewMode === 'list' ? (
          <>
            {/* Domain List */}
            <div className="w-80 border-r border-warm-200 overflow-auto bg-warm-50/30">
              <div className="p-2">
                {(Object.entries(DOMAIN_CONFIG) as [DomainKey, typeof DOMAIN_CONFIG.Market][]).map(
                  ([domain, config]) => {
                    const entities = entitiesByDomain[domain]
                    if (entities.length === 0) return null

                    const Icon = config.icon
                    const isExpanded = expandedDomains.has(domain)

                    return (
                      <div key={domain} className="mb-2">
                        {/* Domain Header */}
                        <button
                          onClick={() => toggleDomain(domain)}
                          className={`w-full flex items-center gap-2 p-3 rounded-lg transition-colors hover:bg-warm-100 ${config.bgColor}`}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-text-muted" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-text-muted" />
                          )}
                          <Icon className={`h-4 w-4 ${config.textColor}`} />
                          <span className={`font-medium ${config.textColor}`}>{domain}</span>
                          <Badge variant="secondary" className="ml-auto text-xs bg-white/50">
                            {entities.length}
                          </Badge>
                        </button>

                        {/* Entity List */}
                        {isExpanded && (
                          <div className="ml-6 mt-1 space-y-1">
                            {entities.map((entity) => (
                              <button
                                key={entity.id}
                                onClick={() => setSelectedEntityId(entity.id)}
                                className={`w-full text-left p-2 rounded-lg transition-all text-sm ${
                                  selectedEntityId === entity.id
                                    ? `${config.bgColor} ring-2 ring-offset-1`
                                    : 'hover:bg-warm-100'
                                }`}
                                style={{
                                  ringColor: selectedEntityId === entity.id ? config.color : undefined,
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium truncate text-text-primary">
                                    {entity.name}
                                  </span>
                                  {entity.status === 'created' && (
                                    <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0" />
                                  )}
                                </div>
                                <div className="text-xs text-text-muted mt-0.5">
                                  {entity.entityType} · {entity.evidence.length} evidence
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  }
                )}
              </div>
            </div>

            {/* Entity Detail Panel */}
            <div className="flex-1 p-4 overflow-auto bg-white">
              {selectedEntity ? (
                <EntityDetailPanel
                  entity={selectedEntity}
                  relationships={doc.relationships.filter(
                    (r) =>
                      r.fromEntityId === selectedEntity.id || r.toEntityId === selectedEntity.id
                  )}
                  entities={doc.entities}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-text-secondary">
                  <div className="text-center">
                    <Target className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>Select an entity to view details</p>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : viewMode === 'doc' ? (
          /* Doc View - Entity list on left, document in center, detail on right */
          <>
            {/* Domain List - Same as List view */}
            <div className="w-72 border-r border-warm-200 overflow-auto bg-warm-50/30">
              <div className="p-2">
                {(Object.entries(DOMAIN_CONFIG) as [DomainKey, typeof DOMAIN_CONFIG.Market][]).map(
                  ([domain, config]) => {
                    const entities = entitiesByDomain[domain]
                    if (entities.length === 0) return null

                    const Icon = config.icon
                    const isExpanded = expandedDomains.has(domain)

                    return (
                      <div key={domain} className="mb-2">
                        {/* Domain Header */}
                        <button
                          onClick={() => toggleDomain(domain)}
                          className={`w-full flex items-center gap-2 p-3 rounded-lg transition-colors hover:bg-warm-100 ${config.bgColor}`}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-text-muted" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-text-muted" />
                          )}
                          <Icon className={`h-4 w-4 ${config.textColor}`} />
                          <span className={`font-medium ${config.textColor}`}>{domain}</span>
                          <Badge variant="secondary" className="ml-auto text-xs bg-white/50">
                            {entities.length}
                          </Badge>
                        </button>

                        {/* Entity List */}
                        {isExpanded && (
                          <div className="ml-6 mt-1 space-y-1">
                            {entities.map((entity) => (
                              <button
                                key={entity.id}
                                onClick={() => {
                                  setSelectedEntityId(entity.id)
                                  // Scroll to evidence in document
                                  if (entity.evidence.length > 0) {
                                    const quote = entity.evidence[0].quote
                                    // Find the highlight element by searching for the entity's first evidence
                                    setTimeout(() => {
                                      const highlights = document.querySelectorAll(`[data-entity-id="${entity.id}"]`)
                                      if (highlights.length > 0) {
                                        highlights[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
                                      }
                                    }, 50)
                                  }
                                }}
                                className={`w-full text-left p-2 rounded-lg transition-all text-sm ${
                                  selectedEntityId === entity.id
                                    ? `${config.bgColor} ring-2 ring-offset-1`
                                    : 'hover:bg-warm-100'
                                }`}
                                style={{
                                  ringColor: selectedEntityId === entity.id ? config.color : undefined,
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium truncate text-text-primary">
                                    {entity.name}
                                  </span>
                                  {entity.status === 'created' && (
                                    <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0" />
                                  )}
                                </div>
                                <div className="text-xs text-text-muted mt-0.5">
                                  {entity.entityType} · {entity.evidence.length} evidence
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  }
                )}
              </div>
            </div>

            {/* Document Content with Highlights */}
            <div className="flex-1 overflow-auto bg-white">
              <DocumentContentView
                content={doc.document.rawContent || doc.document.markdownContent || ''}
                evidenceQuotes={allEvidenceQuotes}
                onEvidenceClick={(entityId) => setSelectedEntityId(entityId)}
                selectedEntityId={selectedEntityId}
              />
            </div>

            {/* Entity Detail Panel (right side) */}
            <div className="w-80 border-l border-warm-200 overflow-auto bg-warm-50/30 p-4">
              {selectedEntity ? (
                <EntityDetailPanel
                  entity={selectedEntity}
                  relationships={doc.relationships.filter(
                    (r) =>
                      r.fromEntityId === selectedEntity.id || r.toEntityId === selectedEntity.id
                  )}
                  entities={doc.entities}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-text-secondary">
                  <div className="text-center">
                    <Target className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Click highlighted text to view entity details</p>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          /* PDF View - Entity list on left, PDF viewer in center, detail on right */
          <>
            {/* Domain List - Same as Doc view */}
            <div className="w-72 border-r border-warm-200 overflow-auto bg-warm-50/30">
              <div className="p-2">
                {(Object.entries(DOMAIN_CONFIG) as [DomainKey, typeof DOMAIN_CONFIG.Market][]).map(
                  ([domain, config]) => {
                    const entities = entitiesByDomain[domain]
                    if (entities.length === 0) return null

                    const Icon = config.icon
                    const isExpanded = expandedDomains.has(domain)

                    return (
                      <div key={domain} className="mb-2">
                        {/* Domain Header */}
                        <button
                          onClick={() => toggleDomain(domain)}
                          className={`w-full flex items-center gap-2 p-3 rounded-lg transition-colors hover:bg-warm-100 ${config.bgColor}`}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-text-muted" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-text-muted" />
                          )}
                          <Icon className={`h-4 w-4 ${config.textColor}`} />
                          <span className={`font-medium ${config.textColor}`}>{domain}</span>
                          <Badge variant="secondary" className="ml-auto text-xs bg-white/50">
                            {entities.length}
                          </Badge>
                        </button>

                        {/* Entity List */}
                        {isExpanded && (
                          <div className="ml-6 mt-1 space-y-1">
                            {entities.map((entity) => (
                              <button
                                key={entity.id}
                                onClick={() => setSelectedEntityId(entity.id)}
                                className={`w-full text-left p-2 rounded-lg transition-all text-sm ${
                                  selectedEntityId === entity.id
                                    ? `${config.bgColor} ring-2 ring-offset-1`
                                    : 'hover:bg-warm-100'
                                }`}
                                style={{
                                  ringColor: selectedEntityId === entity.id ? config.color : undefined,
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium truncate text-text-primary">
                                    {entity.name}
                                  </span>
                                  {entity.status === 'created' && (
                                    <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0" />
                                  )}
                                </div>
                                <div className="text-xs text-text-muted mt-0.5">
                                  {entity.entityType} · {entity.evidence.length} evidence
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  }
                )}
              </div>
            </div>

            {/* PDF Viewer with Evidence Highlights */}
            <div className="flex-1 overflow-hidden bg-warm-100">
              <PdfEvidenceViewer
                documentId={doc.document.id}
                evidenceQuotes={allEvidenceQuotes}
                selectedEntityId={selectedEntityId}
                onEvidenceClick={(entityId) => setSelectedEntityId(entityId)}
              />
            </div>

            {/* Entity Detail Panel (right side) */}
            <div className="w-80 border-l border-warm-200 overflow-auto bg-warm-50/30 p-4">
              {selectedEntity ? (
                <EntityDetailPanel
                  entity={selectedEntity}
                  relationships={doc.relationships.filter(
                    (r) =>
                      r.fromEntityId === selectedEntity.id || r.toEntityId === selectedEntity.id
                  )}
                  entities={doc.entities}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-text-secondary">
                  <div className="text-center">
                    <Target className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Click highlighted text to view entity details</p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// ENTITY DETAIL PANEL
// ============================================================================

interface EntityDetailPanelProps {
  entity: Document2Entity
  relationships: Document2WithEntities['relationships']
  entities: Document2Entity[]
}

function EntityDetailPanel({ entity, relationships, entities }: EntityDetailPanelProps) {
  const domain = getEntityDomain(entity.entityType)
  const config = DOMAIN_CONFIG[domain]
  const entityMap = new Map(entities.map((e) => [e.id, e]))

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge
              className={`${config.bgColor} ${config.textColor} border-0`}
            >
              {entity.entityType}
            </Badge>
            <Badge
              variant="outline"
              className={entity.status === 'created' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-warm-50'}
            >
              {entity.status === 'created' ? 'In Graph' : 'Accepted'}
            </Badge>
            <span className="text-xs text-text-muted">
              {Math.round((entity.confidence || 0.8) * 100)}% confidence
            </span>
          </div>
          <h2 className="text-xl font-semibold text-text-primary">{entity.name}</h2>
        </div>
        <button className="text-text-muted hover:text-text-secondary">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Description */}
      {entity.description && (
        <div>
          <h3 className="text-sm font-medium text-text-secondary mb-1">Content</h3>
          <p className="text-text-primary">{entity.description}</p>
        </div>
      )}

      {/* Evidence */}
      <div>
        <h3 className="text-sm font-medium text-text-secondary mb-2">
          Evidence ({entity.evidence.length})
        </h3>
        <div className="space-y-2">
          {entity.evidence.map((ev, i) => (
            <div
              key={ev.id || i}
              className="bg-solution/5 border-l-4 border-solution p-3 rounded-r-lg"
            >
              <p className="text-sm italic text-text-primary">"{ev.quote}"</p>
            </div>
          ))}
        </div>
      </div>

      {/* Entity Type */}
      <div>
        <h3 className="text-sm font-medium text-text-secondary mb-1">Entity Type</h3>
        <Select defaultValue={entity.entityType}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DOMAIN_CONFIG[domain].types.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Linked Entities (Relationships) */}
      {relationships.length > 0 && (
        <div className="bg-warm-50 rounded-lg p-4 border border-warm-200">
          <h3 className="text-sm font-medium text-text-secondary mb-3 flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            Linked Entities ({relationships.length})
          </h3>
          <div className="space-y-2">
            {relationships.map((rel) => {
              const isFrom = rel.fromEntityId === entity.id
              const otherEntity = entityMap.get(isFrom ? rel.toEntityId : rel.fromEntityId)
              if (!otherEntity) return null

              const otherDomain = getEntityDomain(otherEntity.entityType)
              const otherConfig = DOMAIN_CONFIG[otherDomain]

              return (
                <div
                  key={rel.id}
                  className="flex items-center gap-2 p-2 bg-white rounded-lg border border-warm-200"
                >
                  <Badge className={`${otherConfig.bgColor} ${otherConfig.textColor} border-0 text-xs`}>
                    {otherEntity.entityType}
                  </Badge>
                  <div className="flex-1">
                    <div className="font-medium text-sm text-text-primary">
                      {otherEntity.name}
                    </div>
                    <div className="text-xs text-text-muted">
                      {isFrom ? `→ ${rel.relationshipType}` : `${rel.relationshipType} ←`}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Graph URI */}
      {entity.graphUri && (
        <div className="text-xs text-text-muted font-mono break-all bg-warm-50 p-2 rounded border border-warm-200">
          <span className="text-solution">URI:</span> {entity.graphUri}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// DOCUMENT CONTENT VIEW - Show document text with highlighted evidence
// ============================================================================

interface DocumentContentViewProps {
  content: string
  evidenceQuotes: { quote: string; entityId: string; entityType: string; entityName: string }[]
  onEvidenceClick: (entityId: string) => void
  selectedEntityId: string | null
}

function DocumentContentView({
  content,
  evidenceQuotes,
  onEvidenceClick,
  selectedEntityId,
}: DocumentContentViewProps) {
  // Find page numbers for jump navigation
  const pageNumbers = useMemo(() => {
    const pageMarkerRegex = /---\s*Page\s+(\d+)\s*---/gi
    const pages: number[] = []
    let match
    while ((match = pageMarkerRegex.exec(content)) !== null) {
      pages.push(parseInt(match[1], 10))
    }
    return pages
  }, [content])

  // Highlight evidence quotes in text
  const highlightText = (text: string): React.ReactNode[] => {
    if (evidenceQuotes.length === 0) return [text]

    // Build a map of positions to highlight
    const highlights: {
      start: number
      end: number
      entityId: string
      entityType: string
      entityName: string
    }[] = []

    for (const ev of evidenceQuotes) {
      // Find all occurrences of this quote in the text
      const quote = ev.quote.trim()
      if (quote.length < 3) continue // Skip very short quotes

      let searchStart = 0
      while (true) {
        const idx = text.toLowerCase().indexOf(quote.toLowerCase(), searchStart)
        if (idx === -1) break

        highlights.push({
          start: idx,
          end: idx + quote.length,
          entityId: ev.entityId,
          entityType: ev.entityType,
          entityName: ev.entityName,
        })
        searchStart = idx + 1
      }
    }

    // Sort by start position
    highlights.sort((a, b) => a.start - b.start)

    // Remove overlapping highlights (keep first)
    const nonOverlapping: typeof highlights = []
    for (const h of highlights) {
      const last = nonOverlapping[nonOverlapping.length - 1]
      if (!last || h.start >= last.end) {
        nonOverlapping.push(h)
      }
    }

    // Build result
    const result: React.ReactNode[] = []
    let lastEnd = 0

    for (const h of nonOverlapping) {
      // Add text before highlight
      if (h.start > lastEnd) {
        result.push(text.slice(lastEnd, h.start))
      }

      // Get domain config for styling
      const domain = getEntityDomain(h.entityType)
      const config = DOMAIN_CONFIG[domain]
      const isSelected = selectedEntityId === h.entityId

      // Add highlighted text with data-entity-id for scrolling
      result.push(
        <button
          key={`${h.entityId}-${h.start}`}
          data-entity-id={h.entityId}
          onClick={() => onEvidenceClick(h.entityId)}
          className={`inline underline decoration-2 transition-all cursor-pointer hover:opacity-80 ${
            isSelected ? 'ring-2 ring-offset-1 rounded px-0.5' : ''
          }`}
          style={{
            textDecorationColor: config.color,
            backgroundColor: isSelected ? `${config.color}20` : undefined,
            ringColor: isSelected ? config.color : undefined,
          }}
          title={`${h.entityName} (${h.entityType})`}
        >
          {text.slice(h.start, h.end)}
        </button>
      )
      lastEnd = h.end
    }

    // Add remaining text
    if (lastEnd < text.length) {
      result.push(text.slice(lastEnd))
    }

    return result
  }

  // Transform content to add IDs to page markers for jump navigation
  const contentWithHighlights = useMemo(() => {
    // First, highlight evidence quotes
    const highlighted = highlightText(content)

    // Then wrap page markers with IDs for jump navigation
    return highlighted.map((node, idx) => {
      if (typeof node === 'string') {
        // Check for page markers in the string and wrap them
        const parts = node.split(/(---\s*Page\s+\d+\s*---)/gi)
        if (parts.length > 1) {
          return parts.map((part, partIdx) => {
            const pageMatch = part.match(/---\s*Page\s+(\d+)\s*---/i)
            if (pageMatch) {
              return (
                <span
                  key={`${idx}-${partIdx}`}
                  id={`page-${pageMatch[1]}`}
                  className="block text-text-muted font-medium my-4"
                >
                  {part}
                </span>
              )
            }
            return part
          })
        }
      }
      return node
    })
  }, [content, evidenceQuotes, selectedEntityId])

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Jump to page navigation */}
      {pageNumbers.length > 1 && (
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-warm-200 -mx-6 px-6 py-2 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-text-muted">Jump to:</span>
          {pageNumbers.map((num) => (
            <button
              key={num}
              onClick={() => {
                const el = document.getElementById(`page-${num}`)
                el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }}
              className="px-2 py-0.5 text-xs bg-warm-100 hover:bg-warm-200 rounded transition-colors"
            >
              {num}
            </button>
          ))}
          <span className="text-xs text-text-muted ml-2">+{pageNumbers.length - 10 > 0 ? `${pageNumbers.length - 10} more` : ''}</span>
        </div>
      )}

      {/* Document content with highlights */}
      <div className="text-text-primary whitespace-pre-wrap leading-relaxed text-sm">
        {contentWithHighlights}
      </div>

      {/* Empty state */}
      {content.trim().length === 0 && (
        <div className="text-center text-text-muted py-12">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p>No document content available</p>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// DOCUMENT UPLOADER
// ============================================================================

interface DocumentUploaderProps {
  onClose: () => void
  onDocumentCreated: (doc: Document2WithEntities) => void
  claudeApiKey: string | null
}

function DocumentUploader({ onClose, onDocumentCreated, claudeApiKey }: DocumentUploaderProps) {
  const { accountId, projectId } = useSessionStore()
  const [activeTab, setActiveTab] = useState<'paste' | 'upload'>('paste')
  const [title, setTitle] = useState('')
  const [contentType, setContentType] = useState<ContentType>('custom')
  const [content, setContent] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [metadata, setMetadata] = useState<{
    model: string
    provider: string
    inputTokens: number
    outputTokens: number
    durationMs: number
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    setFile(selectedFile)
    setError(null)
    setLoading(true)
    setProgress('Reading file...')

    try {
      const ext = selectedFile.name.split('.').pop()?.toLowerCase()

      if (ext === 'pdf') {
        const extracted = await extractTextFromPdf(selectedFile)
        setContent(extracted.fullText)
      } else {
        const text = await selectedFile.text()
        setContent(text)
      }

      if (!title) {
        setTitle(selectedFile.name.replace(/\.[^.]+$/, ''))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read file')
    } finally {
      setLoading(false)
      setProgress('')
    }
  }

  const handleSubmit = async () => {
    if (!accountId || !projectId || !claudeApiKey) {
      setError('Missing account/project or Claude API key')
      return
    }

    if (!title.trim()) {
      setError('Title is required')
      return
    }

    if (!content.trim() || content.length < 50) {
      setError('Content must be at least 50 characters')
      return
    }

    try {
      setLoading(true)
      setError(null)
      setMetadata(null)

      // Read file as base64 if it's a PDF
      let fileData: string | undefined
      if (file && getFileType(file.name) === 'pdf') {
        const arrayBuffer = await file.arrayBuffer()
        const bytes = new Uint8Array(arrayBuffer)
        let binary = ''
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i])
        }
        fileData = btoa(binary)
      }

      // 1. Create document
      setProgress('Creating document...')
      const newDoc = await docsApi.create(accountId, projectId, {
        title: title.trim(),
        contentType,
        rawContent: content,
        markdownContent: content,
        fileName: file?.name,
        fileType: file ? getFileType(file.name) : undefined,
        fileData,
      })

      // 2. Analyze with documents3 API
      setProgress('Extracting entities...')
      const result = await docsApi.analyze(accountId, projectId, {
        apiKey: claudeApiKey,
        content,
        contentType,
      })

      if (!result.success) {
        setError(result.error || 'Entity extraction failed')
        setLoading(false)
        return
      }

      setMetadata(result.metadata)

      // 3. Store entities directly in graph (graph-only architecture)
      setProgress(`Storing ${result.entities.length} entities in graph...`)
      await docsApi.storeEntities(accountId, projectId, newDoc.id, {
        entities: result.entities,
        relationships: result.relationships,
      })

      // 4. Load full document
      setProgress('Loading results...')
      const fullDoc = await docsApi.get(accountId, projectId, newDoc.id)

      onDocumentCreated(fullDoc)
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : JSON.stringify(err) || 'Failed to create document'
      setError(errorMessage)
    } finally {
      setLoading(false)
      setProgress('')
    }
  }

  const getFileType = (filename: string): 'pdf' | 'md' | 'txt' | undefined => {
    const ext = filename.split('.').pop()?.toLowerCase()
    if (ext === 'pdf') return 'pdf'
    if (ext === 'md' || ext === 'markdown') return 'md'
    if (ext === 'txt' || ext === 'text') return 'txt'
    return undefined
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Document</DialogTitle>
          <DialogDescription>
            Upload content to extract entities with evidence quotes
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              placeholder="e.g., Q4 Strategy Document"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Content Type */}
          <div className="space-y-2">
            <Label htmlFor="contentType">Content Type</Label>
            <Select value={contentType} onValueChange={(v) => setContentType(v as ContentType)}>
              <SelectTrigger>
                <SelectValue placeholder="Select content type" />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPES.map((ct) => {
                  const IconComponent = CONTENT_TYPE_ICONS[ct.icon]
                  return (
                    <SelectItem key={ct.id} value={ct.id}>
                      <span className="flex items-center gap-2">
                        {IconComponent && <IconComponent className="h-4 w-4 text-text-muted" />}
                        <span>{ct.name}</span>
                      </span>
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Content Input */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'paste' | 'upload')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="paste">Paste Text</TabsTrigger>
              <TabsTrigger value="upload">Upload File</TabsTrigger>
            </TabsList>

            <TabsContent value="paste" className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                placeholder="Paste your document content here..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={10}
                className="font-mono text-sm"
              />
              <p className="text-xs text-text-muted">
                {content.length} characters
                {content.length > 0 && content.length < 50 && (
                  <span className="text-red-500"> (minimum 50)</span>
                )}
              </p>
            </TabsContent>

            <TabsContent value="upload" className="space-y-4">
              <div
                className="border-2 border-dashed border-warm-300 rounded-lg p-8 text-center cursor-pointer hover:border-solution transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.md,.markdown,.pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {file ? (
                  <div>
                    <p className="font-medium text-text-primary">{file.name}</p>
                    <p className="text-sm text-text-muted">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-text-secondary">Click to select a file</p>
                    <p className="text-xs text-text-muted mt-1">Supported: .pdf, .txt, .md</p>
                  </div>
                )}
              </div>

              {content && (
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <div className="border border-warm-200 rounded-lg p-4 max-h-40 overflow-y-auto bg-warm-50">
                    <pre className="text-sm whitespace-pre-wrap font-mono text-text-secondary">
                      {content.slice(0, 800)}
                      {content.length > 800 && '...'}
                    </pre>
                  </div>
                  <p className="text-xs text-text-muted">{content.length} characters loaded</p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Progress */}
          {progress && (
            <div className="flex items-center gap-2 text-sm text-solution">
              <Loader2 className="h-4 w-4 animate-spin" />
              {progress}
            </div>
          )}

          {/* Metadata Display */}
          {metadata && (
            <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-2 text-green-700">
                <CheckCircle2 className="h-4 w-4" />
                <span className="font-medium">Extraction Complete</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs text-green-600">
                <div className="flex items-center gap-1">
                  <Cpu className="h-3 w-3" />
                  {metadata.provider}/{metadata.model}
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {(metadata.durationMs / 1000).toFixed(1)}s
                </div>
                <div className="flex items-center gap-1">
                  <Hash className="h-3 w-3" />
                  {metadata.inputTokens} / {metadata.outputTokens} tokens
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* API Key Warning */}
          {!claudeApiKey && (
            <div className="bg-amber-50 text-amber-700 p-3 rounded-lg text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Claude API key required. Set it in Settings.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !title.trim() || content.length < 50 || !claudeApiKey}
            className="bg-solution hover:bg-solution/90"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              <>
                <Zap className="h-4 w-4 mr-2" />
                Extract Entities
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
